import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    retrieveTrains,
} from "../actions/trains";
import TrainCard from "./TrainCard";
import TrainCalendar from "./TrainCalendar";

const MONTHS_LONG = [
    "januari", "februari", "mars", "april", "maj", "juni",
    "juli", "augusti", "september", "oktober", "november", "december",
];

// Pretty Swedish date label from an ISO ("YYYY-MM-DD") string.
const formatIsoDate = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return `${d} ${MONTHS_LONG[m - 1]} ${y}`;
};

const TrainList = () => {
    const trains = useSelector(state => state.trains);
    const fees = useSelector(state => state.tenant);
    const groupByDate = useSelector(state => state.tenant.groupTrainsByDate);
    const dispatch = useDispatch();
    const [timeoutMessage, setTimeoutMessage] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);

    useEffect(() => {
        dispatch(retrieveTrains());

        // Check for timeout message from sessionStorage
        const message = sessionStorage.getItem('timeoutMessage');
        if (message) {
            setTimeoutMessage(message);
            sessionStorage.removeItem('timeoutMessage');

            // Auto-hide after 10 seconds
            setTimeout(() => setTimeoutMessage(null), 10000);
        }
    }, []);

    const trainData = (trains && trains.data) || [];

    // Distinct ISO dates that have trips, for the calendar indicators.
    const trainDates = useMemo(
        () => [...new Set(trainData.filter(t => t.dateIso).map(t => t.dateIso))],
        [trainData]
    );

    const visibleTrains = groupByDate
        ? trainData.filter(t => t.dateIso === selectedDate)
        : trainData;

    return (
        <>
            {timeoutMessage && (
                <div className="rounded-md bg-yellow-50 p-4 mb-6">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                                {timeoutMessage}
                            </p>
                        </div>
                        <div className="ml-auto pl-3">
                            <div className="-mx-1.5 -my-1.5">
                                <button
                                    type="button"
                                    onClick={() => setTimeoutMessage(null)}
                                    className="inline-flex bg-yellow-50 rounded-md p-1.5 text-yellow-500 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:ring-yellow-600"
                                >
                                    <span className="sr-only">Stäng</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {groupByDate && trainData.length > 0 && (
                <div className="mb-10">
                    <TrainCalendar
                        trainDates={trainDates}
                        selectedDate={selectedDate}
                        onSelectDate={setSelectedDate}
                    />
                    {selectedDate ? (
                        <h2 className="mt-10 text-center text-2xl font-extrabold text-gray-900">
                            Resor {formatIsoDate(selectedDate)}
                        </h2>
                    ) : (
                        <p className="mt-8 text-center text-lg text-gray-500">
                            Välj ett datum i kalendern för att se resor.
                        </p>
                    )}
                </div>
            )}

            <div className="space-y-8">
                {visibleTrains.map((train) => (
                    <TrainCard key={train.id} train={train} fees={fees} />
                ))}
            </div>

            {trains.data && trains.data.length === 0 && (<p>Inga resor finns inplanerade för stunden.</p>)}
        </>
    );
};

export default TrainList;
